import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import ReactTooltip from 'react-tooltip'
import Lightbox from 'yet-another-react-lightbox'
import ReservationImages from './ReservationImages'
import 'yet-another-react-lightbox/styles.css'
import { fromInfo, trans } from '../../../../_providers/Translation'
import { useAuthDataContext } from '../../../../_providers/Auth'
import useReservationInvoices from '../../../../_helpers/useReservationInvoices'

// images
import clientHeadImage from '@/assets/img/reservations/icons/client-head.svg'
import deviceHeadImage from '@/assets/img/reservations/icons/device-head.svg'
import visitpriceHeadImage from '@/assets/img/reservations/icons/visitprice-head.svg'
import hastagImage from '@/assets/img/reservations/icons/hashtag.svg'
import calendarImage from '@/assets/img/reservations/icons/calendar.svg'
import editImage from '@/assets/img/reservations/icons/edit.svg'

// components
import TaskInfoComments from './TaskInfoComments'
import TaskInfoTodos from './TaskInfoTodos'
import { Tooltip } from '@mui/material'
import Help from '@/components/partials/Help'

function Row({ label, children, labelWidthInPx = "120px" }) {
    return (
        <li>
            <span className="label" style={{ width: labelWidthInPx }}>
                {label}
            </span>
            <span className="value">{children || '-'}</span>
        </li>
    )
}

function TruncatedRow({ label, children, id, maxChars = 20 }) {
    const text = children ? String(children) : ''
    const needsTruncation = text.length > maxChars
    const display = needsTruncation ? text.slice(0, maxChars) + '...' : text
    return (
        <li>
            <span className="label">{label}</span>
            <span className="value">
                {needsTruncation
                    ?
                    <Tooltip title={text} placement="top" arrow>
                        <span>{display}</span>
                    </Tooltip>
                    : display || '-'
                }
            </span>
        </li>
    )
}

function Field({ label, children }) {
    return (
        <div className="col">
            <label>{label}</label>
            <div className="read-value">{children || '-'}</div>
        </div>
    )
}

function ViewInfo({ data }) {
    const auth = useAuthDataContext()
    const { invoices } = useReservationInvoices(data.id)
    const [appliancePage, setAppliancePage] = useState(0)
    const [lightboxSlides, setLightboxSlides] = useState([])
    const [lightboxIndex, setLightboxIndex] = useState(-1)

    const openLightbox = (images, idx) => {
        setLightboxSlides(images.map(img => ({ src: img.image_path })))
        setLightboxIndex(idx)
    }
    const editUrl = `/schedule/reservations/edit/${data.id}`
    const currency = data.currency ? data.currency.name : ''

    // normalize appliances to a list whether the API returns an array or single fields
    const appliances = Array.isArray(data.appliances) && data.appliances.length
        ? data.appliances
        : [{
            appliancecategory: data.appliancecategory,
            appliancebrand: data.appliancebrand,
            appliancemodel: data.appliancemodel,
            serial_no: data.serial_no,
            product_code: data.product_code,
        }]

    const performerName = data.performer
        ? (data.performer.type === 'App\\Employee' || data.performer.type === 'App\\Models\\Employee' || data.performer.type === 'employee'
            ? `${data.performer.name || ''} ${data.performer.lastname || ''}`.trim()
            : data.performer.name || '')
        : ''

    const partsFromDisplay = data.parts_provider
        ? (data.parts_provider === 'owner'
            ? trans('labels.ourOwn') || 'Our own'
            : data.parts_provider === 'partner'
                ? trans('labels.partner') || 'Partner'
                : '')
        : ''

    const paymentsTakenByDisplay = data.pos_type
        ? (data.pos_type === 'OWNER'
            ? trans('labels.us') || 'Us'
            : data.pos_type === 'PARTNER'
                ? trans('labels.partner') || 'Partner'
                : '')
        : ''

    const paymentMethodsDisplay = [...new Set(
        (invoices || [])
            .filter(invoice => invoice.paymentmethod)
            .map(invoice => invoice.paymentmethod.translation
                ? invoice.paymentmethod.translation.name
                : invoice.paymentmethod.name)
    )].join(' / ')

    const typeAndTime = [
        data.type && data.type.name ? data.type.name : null,
        data.exec_time_in_minutes ? `${data.exec_time_in_minutes} ${trans('random.min') || 'мин.'}` : null,
    ].filter(Boolean).join(' - ')

    const priceRow = (label, gross, vat, net) => (
        <tr>
            <td>{label}</td>
            <td className="amount">{gross || '0.00'} {currency}</td>
            <td>
                <b>
                    <span>{trans('random.dds')}</span> <span>{vat || '0.00'} {currency}</span>
                </b>
                <b>
                    <span>{trans('random.net')}</span> <span>{net || '0.00'} {currency}</span>
                </b>
            </td>
        </tr>
    )

    return (
        <div id="reservation">
            {data.is_reassigned && auth.partnerId() != data.partner_id && data.partner ?
                <div className="alert warning">
                    {trans('messages.reservationPriority')} <b>{data.partner.name}</b>
                </div>
                :
                ''
            }

            {(data?.appliancecategory?.partner_id && data?.appliancecategory?.partner_id != auth.partnerId() && data?.partner_id != auth.partnerId()) &&
                <div className="alert danger">
                    {trans('random.changeCategory')}
                </div>
            }

            <div className="row">

                {/* Client data */}
                <div className="col">
                    <div className="pannel">
                        <div className="head">
                            <img src={clientHeadImage} alt="client" />
                            {trans('random.clientData')}
                            <div className="right">
                                <Link to={editUrl} className="edit-link">
                                    <img src={editImage} alt="edit" />
                                </Link>
                            </div>
                        </div>
                        {/* Field from here  */}
                        <ul className="view-list">
                            <Row label={trans('labels.name')}>
                                {data.customer ? `${data.customer.name || ''} ${data.customer.lastname || ''}`.trim() : ''}
                            </Row>
                            <Row label={trans('labels.phone')}>
                                {data.phone_formatted || data.phone}
                            </Row>
                            {(data.phones || []).map((phone, idx) => (
                                <Row key={idx} label={trans('labels.new_additional_phone')}>
                                    {[phone.country_code, phone.phone_number].filter(Boolean).join(' ')}
                                    {phone.contact_person_name ? ` (${phone.contact_person_name})` : ''}
                                </Row>
                            ))}
                            <Row label={trans('labels.contractor')}>
                                {data.contractor ? data.contractor.name : ''}
                            </Row>
                            <Row label={trans('labels.clientNo')}>
                                {data.purchase_order}
                            </Row>
                            <Row label={trans('labels.address')}>
                                {data.address}
                            </Row>
                            {data.postcode &&
                                <Row label={trans('labels.postCode')}>
                                    {data.postcode}
                                </Row>
                            }
                            <Row label={trans('labels.email')}>
                                {data.email}
                            </Row>
                        </ul>
                        {data.with_invoice ?
                            <ul className="view-list" style={{ marginTop: '8px', borderTop: '1px solid #f0f0f0', paddingTop: '8px' }}>
                                <Row label={trans('labels.nameForInvoice')}>
                                    {data.invoice_name}
                                </Row>
                                <Row label={trans('labels.eikEgn')}>
                                    {data.invoice_eikegn}
                                </Row>
                                {data.invoice_vat_number &&
                                    <Row label={trans('labels.vatNo')}>
                                        {data.invoice_vat_number}
                                    </Row>
                                }
                                {data.invoice_mol &&
                                    <Row label={trans('labels.mol')}>
                                        {data.invoice_mol}
                                    </Row>
                                }
                                <Row label={trans('labels.address')}>
                                    {data.invoice_address}
                                </Row>
                            </ul>
                            :
                            <>
                            </>
                        }
                        {/* Field to here  */}
                    </div>
                </div>

                {/* Appliances */}
                <div className="col">
                    <div className="pannel">
                        <div className="head">
                            <img src={deviceHeadImage} alt="appliances" />
                            {trans('random.itemToFix')}
                            <div className="right">
                                <Link to={editUrl} className="edit-link">
                                    <img src={editImage} alt="edit" />
                                </Link>
                            </div>
                        </div>
                        {data.subscription?.id ?
                            <>
                                <Field label={trans('labels.subscription')}>
                                    {`${data.subscription.name} - ${data.subscription.ref}`}
                                </Field>
                                <br />
                            </>
                            :
                            <>
                            </>
                        }
                        {data.warranty?.id ?
                            <>
                                <Field label={trans('labels.warranty')}>
                                    {`${data.warranty.name} - ${data.warranty.ref}`}
                                </Field>
                                <br />
                            </>
                            :
                            <>
                            </>
                        }
                        {(() => {
                            const ap = appliances[appliancePage]
                            return (
                                <>
                                    <ul className="view-list">
                                        <Row label={trans('labels.category')}>
                                            {ap.appliancecategory ? ap.appliancecategory.name : ''}
                                        </Row>
                                        <Row label={trans('labels.brand')}>
                                            {ap.appliancebrand ? ap.appliancebrand?.name : ''}
                                        </Row>

                                        <li>
                                            <Row label={trans('labels.model')}>
                                                {ap.appliancemodel ? ap.appliancemodel.name : ap.appliance_model || ''}
                                            </Row>


                                            {ap.serial_no ?
                                                <>
                                                    <div style={{
                                                        backgroundColor: "#f0f0f0",
                                                        width: "1px",
                                                        flexShrink: 0,
                                                        height: "25px",
                                                    }}></div>
                                                    <Row label={trans('labels.serialNo')} labelWidthInPx="fit-content">
                                                        {ap.serial_no}
                                                    </Row>
                                                </>
                                                :
                                                <>
                                                </>
                                            }

                                            {ap.product_code ?
                                                <>
                                                    <div style={{
                                                        backgroundColor: "#f0f0f0",
                                                        width: "1px",
                                                        flexShrink: 0,
                                                        height: "25px",
                                                    }}></div>
                                                    <Row label={trans('labels.productCode')} labelWidthInPx="fit-content">
                                                        {ap.product_code}
                                                    </Row>
                                                </>
                                                :
                                                <>
                                                </>
                                            }
                                        </li>
                                        <TruncatedRow label={trans('labels.description')} id={`ap-desc-${appliancePage}`}>
                                            {ap.description}
                                        </TruncatedRow>
                                        <TruncatedRow label={trans('labels.report')} id={`ap-report-${appliancePage}`}>
                                            {ap.report}
                                        </TruncatedRow>
                                    </ul >
                                    {(ap.images || []).length > 0 && (() => {
                                        const images = ap.images
                                        const MAX_VISIBLE = 10
                                        const visible = images.slice(0, MAX_VISIBLE)
                                        const hidden = images.length - MAX_VISIBLE
                                        return (
                                            <ul className="view-gallery">
                                                {visible.map((img, idx) =>
                                                    <li
                                                        key={idx}
                                                        className="view-gallery-item"
                                                        onClick={() => openLightbox(images, idx)}
                                                    >
                                                        <img src={img.image_path} alt={`appliance-${appliancePage}-img-${idx}`} />
                                                        {idx === MAX_VISIBLE - 1 && hidden > 0 &&
                                                            <div className="view-gallery-overlay">+{hidden}</div>
                                                        }
                                                        <div className="image-name">
                                                            {trans(`labels.${img.uploaded_by}`)}
                                                        </div>
                                                    </li>
                                                )}
                                            </ul>
                                        )
                                    })()
                                    }
                                </>
                            )
                        })()}
                        {appliances.length > 1 &&
                            <div className="appliance-pagination">
                                {appliances.length > 3 &&
                                    <button
                                        className="pagination-arrow"
                                        disabled={appliancePage === 0}
                                        onClick={() => setAppliancePage(p => p - 1)}
                                    >&#8249;</button>
                                }
                                {appliances.map((_, idx) =>
                                    <button
                                        key={idx}
                                        className={'pagination-page' + (idx === appliancePage ? ' active' : '')}
                                        onClick={() => setAppliancePage(idx)}
                                    >{idx + 1}</button>
                                )}
                                {appliances.length > 3 &&
                                    <button
                                        className="pagination-arrow"
                                        disabled={appliancePage === appliances.length - 1}
                                        onClick={() => setAppliancePage(p => p + 1)}
                                    >&#8250;</button>
                                }
                            </div>
                        }
                    </div>
                </div>
            </div>

            {/* Comments & Todos (reuse the interactive partials) */}
            <div className="row">
                <div className="col">
                    <TaskInfoComments reservationId={data.id} data={data} />
                </div>
                <div className="col">
                    <TaskInfoTodos reservationId={data.id} data={data} />
                </div>
            </div>

            {/* Visit & Price */}
            <div className="row">
                <div className="col">
                    <div className="pannel total-field">
                        <div className="head">
                            <img src={visitpriceHeadImage} alt="visitation" />
                            {trans('random.visitAndPrice')}
                            <div className="right" style={{ marginLeft: 10 }}>
                                <Link to={editUrl} className="edit-link">
                                    <img src={editImage} alt="edit" />
                                </Link>
                            </div>
                            {data.reference_code &&
                                <div className="ref-number">
                                    <img src={hastagImage} alt="ref number" />
                                    <div className="right">
                                        <span>{trans('labels.refNumber')}</span>
                                        <b>{data.reference_code}</b>
                                    </div>
                                </div>
                            }
                            {data.created_dmy &&
                                <div className="created-at">
                                    <img src={calendarImage} alt="calendar" />
                                    <div className="right">
                                        <span>{trans('labels.creationDate')}</span>
                                        <b>{data.created_dmy}</b>
                                    </div>
                                </div>
                            }
                        </div>

                        <div className="row">
                            <Field label={trans('labels.visitDate')}>{data.visit_date_dmy}</Field>
                            <Field label={trans('labels.visitHour')}>{data.visitperiod ? data.visitperiod.period : ''}</Field>
                            <Field label={trans('labels.setTo')}>{performerName}</Field>
                            {data.performer && (data.performer.type === 'App\\Partner' || data.performer.type === 'App\\Models\\Partner') &&
                                <Field label={trans('labels.partsFrom')}>{partsFromDisplay}</Field>
                            }
                            {data.performer && (data.performer.type === 'App\\Partner' || data.performer.type === 'App\\Models\\Partner') &&
                                <Field label={trans('labels.paymentWillBeTakenFrom')}>{paymentsTakenByDisplay}</Field>
                            }
                            <Field label={trans('labels.initialStatus')}>
                                {data.initialstatus && data.initialstatus.translation ? data.initialstatus.translation.name : ''}
                            </Field>
                            <Field label={trans('labels.finalStatus')}>
                                {data.finalstatus && data.finalstatus.translation ? data.finalstatus.translation.name : ''}
                            </Field>
                        </div>

                        <div className="row">
                            <Field label={trans('labels.typeAndTime')}>{typeAndTime}</Field>
                        </div>

                        <div className="total-price">
                            <div className="left">
                                <div className="table-scroller">
                                    <table>
                                        <tbody>
                                            {data.diagnostic_price > 0 &&
                                                priceRow(
                                                    trans('labels.diagnosticPrice'),
                                                    data.diagnostic_price_formatted,
                                                    data.diagnostic_vat_price_formatted,
                                                    data.diagnostic_net_price_formatted
                                                )
                                            }
                                            {!(data.is_copy && !Number(data.can_see_labour_price)) &&
                                                priceRow(
                                                    trans('labels.labourPrice'),
                                                    data.labour_price_formatted,
                                                    data.labour_vat_price_formatted,
                                                    data.labour_net_price_formatted
                                                )
                                            }
                                            {data.transport_price > 0 &&
                                                priceRow(
                                                    trans('labels.transportPrice'),
                                                    data.transport_price_formatted,
                                                    data.transport_vat_price_formatted,
                                                    data.transport_net_price_formatted
                                                )
                                            }
                                            {!(data.is_copy && !Number(data.can_see_part_prices)) &&
                                                priceRow(
                                                    trans('labels.partsPrice'),
                                                    data.parts_price_formatted,
                                                    data.parts_vat_price_formatted,
                                                    data.parts_net_price_formatted
                                                )
                                            }
                                            {(data.other_expenses || []).map(oe =>
                                                priceRow(
                                                    `${oe.name}${oe.quantity ? ` x ${oe.quantity}` : ''}`,
                                                    oe.total_gross_price_formatted,
                                                    oe.vat_price_formatted,
                                                    oe.net_price_formatted
                                                )
                                            )}
                                            <tr>
                                                <td colspan="4">
                                                    <hr
                                                        style={{
                                                            margin: 0
                                                        }}
                                                    />
                                                </td>
                                            </tr>

                                            {(data.sanctions || []).map((oe, i) =>
                                                <tr key={oe.id}>
                                                    <td>{`${trans('labels.sanction')} #${i + 1}`}</td>
                                                    <td className="amount" colspan="3">{oe.amount} {currency} <Help>{fromInfo(oe.comment)}</Help></td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                            <div className="right">
                                <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                                    <table className="gross">
                                        <tbody>
                                            <tr>
                                                <td>{trans('random.paidGross')}</td>
                                                <td>{data.paid_gross_price_formatted || '0.00'} {currency}</td>
                                            </tr>
                                            <tr>
                                                <td>{trans('random.leftToPayGross')}</td>
                                                <td>{data.unpaid_gross_price_formatted || '0.00'} {currency}</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                    <div style={{
                                        flexShrink: 0,
                                        marginLeft: '50px',
                                        textAlign: 'right'
                                    }}>
                                        <b>{data.total_price_formatted || '0.00'} {currency}</b>
                                        <table className="net">
                                            <tbody>
                                                <tr>
                                                    <td>{trans('random.dds')}</td>
                                                    <td>{data.total_vat_price_formatted || '0.00'} {currency}</td>
                                                </tr>
                                                <tr>
                                                    <td>{trans('random.net')}</td>
                                                    <td>{data.total_net_price_formatted || '0.00'} {currency}</td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                                <hr />
                                <table
                                    className="gross"
                                    style={{
                                        width: 'fit-content',
                                    }}
                                >
                                    <tbody>
                                        <tr>
                                            <td>Статус на плащане:</td>
                                            <td
                                                style={{
                                                    textAlign: 'left'
                                                }}
                                            >
                                                {data.is_paid ? 'Платена' : 'Неплатена'}
                                            </td>
                                        </tr>
                                        {data.is_paid ?
                                            <tr>
                                                <td>Метод на плащане:</td>
                                                <td
                                                    style={{
                                                        textAlign: 'left'
                                                    }}
                                                >
                                                    {paymentMethodsDisplay || '-'}
                                                </td>
                                            </tr>
                                            :
                                            <></>
                                        }
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="row">
                <div className="col">
                    <div className="pannel">
                        <div className="head">
                            <img src={visitpriceHeadImage} alt="visitation" />
                            {trans('tabs.images')}
                        </div>
                        <ReservationImages data={data} />
                    </div>
                </div>
            </div>

            <Lightbox
                open={lightboxIndex >= 0}
                index={lightboxIndex}
                close={() => setLightboxIndex(-1)}
                slides={lightboxSlides}
            />
        </div >
    )
}

export default ViewInfo
